import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { Children } from 'react';
import { compose, layout, space } from 'styled-system';

import Icon from '../Icon';

const StyledBreadcrumb = styled('nav')`
  ${compose(space, layout)}
`;

const StyledOrderedList = styled('ol')`
  ${({ theme }) => css`
    display: flex;
    flex-flow: row nowrap;
    list-style: none;
    width: 100%;
    overflow-x: auto;
  }
  ${theme.mediaQueries.md} {
    overflow: hidden;
    // Hide scrollbar for IE, Edge and Firefox
    -ms-overflow-style: none;
    scrollbar-width: none;
    // Hide scrollbar for Chrome, Safari and Opera
    &::-webkit-scrollbar {
      display: none;
    }
  `}
`;

const StyledNavItem = styled('li')`
  ${({ theme, unique }) => css`
    align-items: center;
    color: ${theme.colors.breadcrumb.disabled.foreground};
    cursor: pointer;
    display: ${unique ? 'display-inline' : 'flex'};
    font-size: ${theme.typography.texts.size2.fontSize};
    line-height: ${theme.typography.texts.size2.lineHeight};
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;

    a {
      color: ${theme.colors.breadcrumb.active.foreground};
      text-decoration: none;
    }

    &:hover {
      text-decoration: underline;
    }

    &:nth-last-of-type(2) {
      display: inline-flex;
    }

    &:last-child {
      color: ${theme.colors.breadcrumb.disabled.foreground};
      cursor: default;
      padding-right: ${theme.space[4]};

      &:hover {
        text-decoration: none;
      }

      & > :first-of-type {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      ${theme.mediaQueries.md} {
        overflow: hidden;
        padding-right: 0;
      }
    }
  `}
`;

function Breadcrumb({ children, ...rest }) {
  // If there's a `false` children, it is commented
  // and therefore we shouldn't render
  const activeChildrenArr =
    Array.isArray(children) && children.filter((child) => child);

  return (
    <StyledBreadcrumb
      aria-label="breadcrumb"
      pb={4}
      pt={[6, null, null, 6]}
      {...rest}
    >
      <StyledOrderedList>
        {Array.isArray(children) ? (
          Children.map(activeChildrenArr, (child, i) => {
            const lastChild = activeChildrenArr.length - 1;

            return (
              // eslint-disable-next-line react/no-array-index-key
              <StyledNavItem key={i}>
                {child}
                {lastChild !== i && <Icon name="arrow-forward" size="size3" />}
              </StyledNavItem>
            );
          })
        ) : (
          <StyledNavItem unique>{children}</StyledNavItem>
        )}
      </StyledOrderedList>
    </StyledBreadcrumb>
  );
}

Breadcrumb.propTypes = {
  /**
   * Components to be rendered inside component
   */
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.element])
    .isRequired,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
};

export default Breadcrumb;
